import type { StartAnfrage } from '@awf/shared';
import { type FormEvent, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { ApiError } from '../api/client.js';
import { verhaltenFuer } from '../api/errors.js';
import { AUFTRAGSTYPEN } from '../auftragstypen.js';
import { TYP_TEXT } from '../runs/status.js';
import { LinienSymbol } from '../ui/linien-symbol.js';
import { Seitenkopf } from '../ui/seitenkopf.js';
import seite from '../ui/seite.module.css';
import { AblaufSpalte } from './ablauf-spalte.js';
import {
  FELDSAETZE,
  istGesperrt,
  istGueltigerTyp,
  themaAus,
  type FeldSpalte,
} from './field-sets.js';
import stile from './run-form-page.module.css';
import {
  alsStartAnfrage,
  leereWerte,
  pruefeFormular,
  type Feldfehler,
  type FormularWerte,
} from './validate.js';

const KONTINGENT_TEXT =
  'Sie haben das Kontingent von 10 Läufen pro Stunde erreicht. Bitte versuchen Sie es später erneut, Ihre Eingaben bleiben erhalten.';

function Feld({
  spalte,
  werte,
  fehler,
  setzen,
}: {
  spalte: FeldSpalte;
  werte: FormularWerte;
  // `| undefined` ausdruecklich: `exactOptionalPropertyTypes` unterscheidet "Feld fehlt"
  // von "Feld ist undefined", und `find()` liefert genau Letzteres.
  fehler?: Feldfehler | undefined;
  setzen: (name: keyof FormularWerte, wert: string | boolean) => void;
}) {
  const id = `feld-${spalte.name}`;
  const gesperrt = istGesperrt(spalte, werte);
  // Gesperrt verdraengt der Sperrgrund den Hinweis: Er sagt, wie das Feld wieder frei wird.
  const hinweis = gesperrt ? spalte.gesperrtDurch?.hinweis : spalte.hinweis;
  const hinweisId = hinweis || fehler ? `${id}-hinweis` : undefined;

  if (spalte.art === 'schalter') {
    return (
      <div className={stile.schalter}>
        <input
          id={id}
          type="checkbox"
          checked={werte[spalte.name] as boolean}
          onChange={(e) => setzen(spalte.name, e.target.checked)}
        />
        <label htmlFor={id}>{spalte.beschriftung}</label>
      </div>
    );
  }

  const wert = werte[spalte.name] as string;

  return (
    <div className={`eds-field${fehler ? ' eds-field--error' : ''}`}>
      <label className="eds-field__label" htmlFor={id}>
        {spalte.beschriftung}
        {spalte.pflicht && (
          <span className={stile.pflichtstern} aria-hidden="true">
            {' *'}
          </span>
        )}
      </label>

      {spalte.art === 'auswahl' && (
        <select
          className="eds-field__input"
          id={id}
          value={wert}
          disabled={gesperrt}
          aria-invalid={fehler ? true : undefined}
          aria-describedby={hinweisId}
          onChange={(e) => setzen(spalte.name, e.target.value)}
        >
          {spalte.optionen?.map((o) => (
            <option key={o.wert} value={o.wert}>
              {o.text}
            </option>
          ))}
        </select>
      )}

      {spalte.art === 'mehrzeilig' && (
        <textarea
          className={`eds-field__input ${stile.mehrzeilig}`}
          id={id}
          value={wert}
          placeholder={spalte.platzhalter}
          maxLength={spalte.grenze}
          aria-invalid={fehler ? true : undefined}
          aria-describedby={hinweisId}
          onChange={(e) => setzen(spalte.name, e.target.value)}
        />
      )}

      {spalte.art === 'text' && (
        <input
          className="eds-field__input"
          id={id}
          type="text"
          value={wert}
          disabled={gesperrt}
          placeholder={spalte.platzhalter}
          maxLength={spalte.grenze}
          aria-invalid={fehler ? true : undefined}
          aria-describedby={hinweisId}
          onChange={(e) => setzen(spalte.name, e.target.value)}
        />
      )}

      {(hinweis || fehler) && (
        <p
          className={`eds-field__hint${gesperrt && !fehler ? ` ${stile.sperrHinweis}` : ''}`}
          id={hinweisId}
        >
          {fehler ? fehler.text : hinweis}
        </p>
      )}
    </div>
  );
}

export function RunFormPage({
  starte,
}: {
  starte: (anfrage: StartAnfrage) => Promise<{ runId: string }>;
}) {
  const { typ: rohTyp = '' } = useParams();
  const navigiere = useNavigate();

  const gueltig = istGueltigerTyp(rohTyp);
  const [werte, setWerte] = useState<FormularWerte>(() =>
    leereWerte(gueltig ? rohTyp : 'ratgeber'),
  );
  const [feldfehler, setFeldfehler] = useState<Feldfehler[]>([]);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  if (!gueltig) return <Navigate to="/neu" replace />;

  const spalten = FELDSAETZE[rohTyp];

  function setzen(name: keyof FormularWerte, wert: string | boolean) {
    setWerte((alt) => ({ ...alt, [name]: wert }));
    setFeldfehler((alt) => alt.filter((f) => f.feld !== name));
  }

  function abschicken(ereignis: FormEvent) {
    ereignis.preventDefault();
    setMeldung(null);

    const vollstaendig = { ...werte, thema: themaAus(werte) };
    const gefunden = pruefeFormular(vollstaendig);
    if (gefunden.length > 0) {
      setFeldfehler(gefunden);
      return;
    }

    setFeldfehler([]);
    setSendet(true);
    void starte(alsStartAnfrage(vollstaendig))
      .then(({ runId }) =>
        navigiere(`/auftraege/${runId}`, {
          replace: true,
          state: { bestaetigung: 'Ihr Auftrag ist gestartet.' },
        }),
      )
      .catch((fehler: unknown) => {
        if (!(fehler instanceof ApiError)) {
          setMeldung('Der Auftrag konnte nicht gestartet werden. Bitte versuchen Sie es erneut.');
          return;
        }
        setMeldung(verhaltenFuer(fehler.code) === 'kontingent' ? KONTINGENT_TEXT : fehler.text);
      })
      .finally(() => setSendet(false));
  }

  return (
    <>
      <Seitenkopf
        titel={`${TYP_TEXT[rohTyp]} starten`}
        pfad={[
          { text: 'Übersicht', ziel: '/' },
          { text: 'Neuer Auftrag', ziel: '/neu' },
        ]}
        beschreibung={AUFTRAGSTYPEN[rohTyp].erklaerung}
      />

      {/* Formular im DOM zuerst: Breit steht es links, Lese- und Tab-Reihenfolge folgen ihm. Schmal
          stellt das Raster die Spalte darueber (grid-template-areas), Kleinkram P3. */}
      <div className={stile.raster}>
        <form className={`${seite.karte} ${stile.formular}`} onSubmit={abschicken} noValidate>
          {spalten.map((spalte) => (
            <Feld
              key={spalte.name}
              spalte={spalte}
              werte={werte}
              fehler={feldfehler.find((f) => f.feld === spalte.name)}
              setzen={setzen}
            />
          ))}

          {/* Direkt ueber dem Startknopf statt ueber der ganzen Seite: Dort, wo geklickt wurde,
              steht auch die Antwort, und die Meldung bleibt so breit wie das Formular. */}
          {meldung && (
            <div className={`eds-alert eds-alert--error ${stile.meldung}`} role="alert">
              <span className={stile.meldungSymbol}>
                <LinienSymbol name="alert" groesse={20} />
              </span>
              <div className={stile.meldungText}>
                <p className="eds-alert__title">Ihr Auftrag wurde nicht gestartet</p>
                <p>{meldung}</p>
              </div>
            </div>
          )}

          <div className={stile.aktionen}>
            {/* Gesperrt, solange gesendet wird: Ein Doppelklick wuerde zwei Laeufe starten
                und zwei der zehn Kontingentplaetze kosten. */}
            <button className="eds-button eds-button--primary" type="submit" disabled={sendet}>
              {sendet ? 'Wird gestartet …' : `${TYP_TEXT[rohTyp]} starten`}
            </button>
            <Link className="eds-button eds-button--tertiary" to="/">
              Abbrechen
            </Link>
          </div>
        </form>
        <AblaufSpalte typ={rohTyp} />
      </div>
    </>
  );
}

/** Ein Typwechsel ueber die Seitenleiste behaelt sonst die Instanz -- und damit den alten `typ` im Zustand. */
export function RunFormRoute(props: {
  starte: (anfrage: StartAnfrage) => Promise<{ runId: string }>;
}) {
  const { typ = '' } = useParams();
  return <RunFormPage key={typ} {...props} />;
}
